/**
 * VIP权限系统API服务
 * 处理VIP相关的API调用
 */

import type {
  PermissionInfoResponse,
  VIPGiftRule,
  VIPActivationInput,
} from '../types/vip.js';
import { signRequest } from '../utils/apiSignature.js';
import { useAuthStore } from '../stores/auth.js';

/**
 * API响应格式
 */
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
}

/**
 * VIP API服务类
 */
export class VIPApiService {
  private baseUrl: string;

  constructor() {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    this.baseUrl = `${protocol}//${hostname}${port ? `:${port}` : ""}`;
  }

  /**
   * 通用请求方法（带签名）
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const method = options.method || 'GET';

    // 从 authStore 获取 token
    const token = useAuthStore.getState().accessToken;

    // 调试日志
    console.log('[VIP API] 请求信息:', {
      endpoint,
      method,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'null',
    });

    // 解析请求体和查询参数
    let body: any = undefined;
    if (options.body) {
      try {
        body = JSON.parse(options.body as string);
      } catch {
        body = options.body;
      }
    }

    // 构建请求头
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string>),
    };

    // 📝 所有请求都需要API签名(包括已认证的请求)
    // 签名提供额外的安全层,防止请求篡改和重放攻击
    const signatureHeaders = await signRequest(
      url,
      method,
      body,
      {} // query参数已经在url中
    );
    Object.assign(headers, signatureHeaders);

    const defaultOptions: RequestInit = {
      ...options,
      headers,
    };

    const response = await fetch(url, defaultOptions);

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData: ApiResponse = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // 如果无法解析错误响应，使用默认错误消息
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * 获取用户权限信息
   * GET /api/auth/permissions
   */
  static async getPermissions(): Promise<PermissionInfoResponse> {
    const client = new VIPApiService();
    const response: ApiResponse<PermissionInfoResponse> = await client.request("/api/auth/permissions");
    if (!response.success || !response.data) {
      throw new Error("获取权限信息失败");
    }
    return response.data;
  }

  /**
   * 刷新权限状态
   * POST /api/auth/refresh-permissions
   */
  static async refreshPermissions(): Promise<PermissionInfoResponse> {
    const client = new VIPApiService();
    const response: ApiResponse<PermissionInfoResponse> = await client.request("/api/auth/refresh-permissions", {
      method: "POST",
    });
    if (!response.success || !response.data) {
      throw new Error("刷新权限失败");
    }
    return response.data;
  }

  /**
   * 激活VIP
   * POST /api/vip/activate
   */
  static async activateVIP(input: VIPActivationInput): Promise<{
    user: any;
    permissionInfo: PermissionInfoResponse;
  }> {
    const client = new VIPApiService();
    const response: ApiResponse<{
      user: any;
      permissionInfo: PermissionInfoResponse;
    }> = await client.request("/api/vip/activate", {
      method: "POST",
      body: JSON.stringify(input),
    });
    if (!response.success || !response.data) {
      throw new Error("激活VIP失败");
    }
    return response.data;
  }

  /**
   * 获取VIP激活历史
   * GET /api/vip/history
   */
  static async getActivationHistory(limit = 10): Promise<{
    history: any[];
    total: number;
  }> {
    const client = new VIPApiService();
    const url = `/api/vip/history?limit=${limit}`;
    const response: ApiResponse<{
      history: any[];
      total: number;
    }> = await client.request(url);
    if (!response.success || !response.data) {
      throw new Error("获取激活历史失败");
    }
    return response.data;
  }

  /**
   * 获取VIP赠送规则列表
   * GET /api/vip/gift-rules
   */
  static async getGiftRules(): Promise<{
    rules: VIPGiftRule[];
    total: number;
  }> {
    const client = new VIPApiService();
    const response: ApiResponse<{
      rules: VIPGiftRule[];
      total: number;
    }> = await client.request("/api/vip/gift-rules");
    if (!response.success || !response.data) {
      throw new Error("获取赠送规则失败");
    }
    return response.data;
  }

  /**
   * 获取VIP统计信息(管理员)
   * GET /api/vip/statistics
   */
  static async getStatistics(): Promise<{
    total: number;
    free: number;
    vip: number;
    vvip: number;
    admin: number;
    expired: number;
  }> {
    const client = new VIPApiService();
    const response: ApiResponse<{
      total: number;
      free: number;
      vip: number;
      vvip: number;
      admin: number;
      expired: number;
    }> = await client.request("/api/vip/statistics");
    if (!response.success || !response.data) {
      throw new Error("获取统计信息失败");
    }
    return response.data;
  }
}

// 导出单例
export const vipApiService = VIPApiService;
